import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AxiosError } from "axios";
import moment from "moment";

import {
  getEnvironment,
  isFieldEnrolled,
  useAddFieldCropPractice,
  useAddFieldPracticeParticipations,
  useDeleteFieldCropPractice,
  useDeleteFieldPracticeParticipations,
  useGetAllCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFieldPractices,
  useGetFieldShape,
  useGetVerificationEditingRequests,
  useLocation,
  useNavigate,
  useTranslation,
  useUpdateFieldCropPractice,
  useUpdateVerificationEditingRequest,
  useValidateCropPeriods,
} from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CropFCPO,
  CropFormValidationError,
  EDITING_REQUEST_TYPE,
  FertilizerManagement,
  FieldListField,
  PRACTICE_BUNDLE_SLUG,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeCrop,
  PracticeFCPO,
  VIEW_TYPE,
  ZERO_UUID,
} from "models";

import { Button, Drawer, FieldHeadline, Form, HideFCPOFeatureForUnenrolledFarms, Modal, Skeleton, StickyFooterButtons, Message as message } from "components";

import { PracticeBundleControls } from "pages/app/all-field-view/components/PracticeBundleControls";
import { PracticeEnrolmentNudgeCard } from "pages/app/all-field-view/components/PracticeEnrolmentNudgeCard";
import { AddEditCropForm } from "pages/app/all-field-view/components/forms/crop/AddEditCropForm";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers";
import { FertilizerManagementYesNoForm } from "pages/app/all-field-view/components/forms/fertilizers/FertilizerManagementYesNoForm";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { createCropPracticeRequestBody } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { getCropRecommendedDates, getIsBaselineOrHistoricYear } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import { getInputRequirementConfig, isRequired, shouldShowInput } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";
import { PRACTICE_TYPE } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils.ts";
import { isDateValidConsideringLastConfirmedCrop } from "pages/app/field-timeline/utils/TimelineUtils.ts";

type FormPracticeObject = Record<string, Record<string, string | moment.Moment[]>>;

type FieldError = { name: string[]; errors: string[] };

const bundleToSlugMap = {
  crops: [PRACTICE_SLUG.CROPS],
  tillage: [PRACTICE_SLUG.SEEDINGS, PRACTICE_SLUG.TILLAGES],
  fertilizer: [PRACTICE_SLUG.MINERAL_FERTILIZERS, PRACTICE_SLUG.ORGANIC_FERTILIZERS],
};

const currentYear = moment().year();

const VALIDATION_DELAY_MS = 100;
const DRAWER_SCROLL_DELAY_MS = 100;
const MAX_VALIDATION_ATTEMPTS = 10;

export const AddEditCropDrawer = ({
  availablePracticeBundles,
  field,
  existingFertilizers, // to be used in forms
  existingTillages, // to be used in forms
  existingCrop, // to be used in forms
  allCrops,
  showDrawer,
  setShowDrawer,
  year,
  viewType,
  type,
  fertilizerManagements = [],
  simulationId,
  formWithOnLoadValidation = false,
  isMandatoryCropCard = false,
  carbonProgram,
  farmId,
}: {
  availablePracticeBundles: PracticeBundle[];
  field: FieldListField;
  existingFertilizers: PracticeFCPO[];
  existingTillages: PracticeFCPO[];
  existingCrop: CropFCPO; // one crop object
  allCrops: CropFCPO[];
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
  year: number;
  viewType?: VIEW_TYPE;
  type?: PRACTICE_TYPE;
  fertilizerManagements: FertilizerManagement[];
  simulationId?: string;
  formWithOnLoadValidation?: boolean;
  isMandatoryCropCard?: boolean;
  carbonProgram: CarbonProgram | null | undefined;
  farmId: string | undefined;
}) => {
  const { t } = useTranslation(["allFieldView"]);
  const [form] = Form.useForm();
  const { validatePeriods } = useValidateCropPeriods();
  const { cmcMixCropFCPO, selectedRecommendationItemOnTimeline, showDrawerFromCMC, setShowDrawerFromCMC } = useCropMixComposerContext();
  const isSideCropRecommendationPlaceholder = selectedRecommendationItemOnTimeline?.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION;

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const { fieldShape, fieldShapeLoading } = useGetFieldShape({ fieldId: field.id });
  const { mutate: addFieldCropPractice } = useAddFieldCropPractice({ fieldId: field.id, farmId, simulationId });
  const { mutate: updateFieldCropPractice } = useUpdateFieldCropPractice({ fieldId: field.id, farmId, simulationId });
  const { mutateAsync: deleteFieldCropPractice } = useDeleteFieldCropPractice({ fieldId: field.id, farmId, simulationId });
  const { mutate: addFieldPracticeParticipations } = useAddFieldPracticeParticipations(field.id, farmId);
  const { mutate: deleteFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(field.id, farmId);

  const { data: fieldPractices } = useGetFieldPractices({ fieldId: field.id ?? "", inView: true });

  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId: farmId ?? "" });
  const { mutateAsync: submitEditingRequestComment } = useUpdateVerificationEditingRequest({ farmId: farmId ?? "" });

  // we can refactor to a helper function && move to a util if needed in another place, but let's keep it here for now
  const pendingCropVerificationRequest = editingRequests?.find(
    (r) =>
      r.requestType === EDITING_REQUEST_TYPE.CROP &&
      r.completedAt === null &&
      (r.fieldCropId === existingCrop.id ||
        (!existingCrop.id && r.fieldCropId === null && r.year === year && r.fieldId === field.id && r.cropType === existingCrop.cropType))
  );

  const { data: activeFarmReportingYear } = useGetFarmReportingYear(farmId || null);

  const { isSubmissionFeatureEnabled, isDataSubmitted } = useSubmissionStatus(activeFarmReportingYear);

  const mineralFertilizers = existingFertilizers.filter((fertilizer) => fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const organicFertilizers = existingFertilizers.filter((fertilizer) => fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  const farmIsEnrolledInCarbonProgram = !!carbonProgram?.id;
  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  // We need the carbon runner outcomes to figure out which inputs should be required
  const { data: carbonProgramOutcomes, isLoading: isLoadingProgramOutcomes } = useGetAllCarbonProgramOutcomes(farmId, carbonProgram);

  const carbonProgramResultOfField = carbonProgramOutcomes?.[simulationId ?? field.id];

  const isCurrentYear = year === currentYear;

  const [fieldsTouched, setFieldsTouched] = useState<boolean>(false);
  const [cropPeriodValidationErrors, setCropPeriodValidationErrors] = useState<CropFormValidationError[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [optionalInputsAreShown, setOptionalInputsAreShown] = useState<boolean>(false);
  const [initialValidationDone, setInitialValidationDone] = useState<boolean>(false);
  const [practiceBundles, setPracticeBundles] = useState(() =>
    availablePracticeBundles.length > 0 ? availablePracticeBundles : fieldPractices?.availablePracticeBundles ?? []
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<string>(existingCrop.seedingStartDate);
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<string>(existingCrop.harvestEndDate);

  const tillageRef = useRef<HTMLDivElement | null>(null);
  const fertilizerRef = useRef<HTMLDivElement | null>(null);
  const seedingRef = useRef<HTMLDivElement | null>(null);

  const baselineYearsByPracticeType = useMemo(
    () => (carbonProgramResultOfField ? getBaselineYearsByPracticeType(carbonProgramResultOfField) : {}),
    [carbonProgramResultOfField]
  );

  const handleBundleActiveStateChange = async (bundleSlug: PRACTICE_BUNDLE_SLUG, value: boolean) => {
    setFieldsTouched(true);

    if (value) {
      addFieldPracticeParticipations({ fieldId: field.id, practiceSlugs: bundleToSlugMap[bundleSlug] });
    } else {
      deleteFieldPracticeParticipations({ fieldId: field.id, practiceSlugs: bundleToSlugMap[bundleSlug] });
    }

    const updatedBundle = { ...practiceBundles.find((bundle) => bundle.slug === bundleSlug)!, active: value };
    const newArray = [...practiceBundles.filter((bundle) => bundle.slug !== bundleSlug), updatedBundle];

    setPracticeBundles(newArray);
  };

  // Toggle handler for past year inputs
  const handlePastYearToggle = useCallback(() => {
    setFieldsTouched(true);
  }, []);

  const handleCropDeletion = useCallback(async () => {
    if (!field?.id || !existingCrop?.id) return;

    try {
      await deleteFieldCropPractice({ cropId: existingCrop.id });
      message({ content: t("cropForm.cropDeletionModal.success"), icon: "tick" });
      setShowDrawer(false);

      if (existingCrop.cropMixId) {
        navigate(basePath);
      }

      if (showDrawerFromCMC) {
        setShowDrawerFromCMC(false);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.code === "ERR_CANCELED") {
        message({ content: t("cropForm.cropDeletionModal.fail"), icon: "error" });
      }
    }
  }, [
    basePath,
    existingCrop?.cropMixId,
    navigate,
    field?.id,
    existingCrop?.id,
    deleteFieldCropPractice,
    t,
    setShowDrawer,
    showDrawerFromCMC,
    setShowDrawerFromCMC,
  ]);

  const onSubmit = async () => {
    setIsLoading(true);

    const cropFromFormField = form.getFieldValue("crop");
    const cropToValidate = {
      ...existingCrop,
      seedingStartDate: cropFromFormField?.seedingDateRange[0],
      harvestEndDate: cropFromFormField?.harvestDateRange[1],
    };
    const cropPeriodValidationErrors = validatePeriods(cropToValidate, allCrops || []);

    if (!cropFromFormField?.seedingDateRange[0] || !cropFromFormField?.harvestDateRange[1]) {
      cropPeriodValidationErrors.push({ fieldId: field.id, message: t("errors.required") });
      setCropPeriodValidationErrors(cropPeriodValidationErrors);
      setIsLoading(false);
      message({ content: t("errors.required"), icon: "error" });
      return;
    }

    const isAfterLastConfirmedCrop = isDateValidConsideringLastConfirmedCrop(
      cropFromFormField?.seedingDateRange[0],
      cropFromFormField?.harvestDateRange[1],
      allCrops,
      cropFromFormField?.cropType,
      cropToValidate?.year
    );
    if (!cropToValidate.isConfirmed && !isAfterLastConfirmedCrop) {
      cropPeriodValidationErrors.push({ fieldId: field.id, message: t("cropForm.errorMessages.notAllowedToMoveBeforeConfirmationDate") });
    }

    // check if there's already a main crop added to the target year
    const isMainCrop = cropFromFormField.cropType === CROP_TYPE_FCPO.MAIN;

    if (isMainCrop) {
      const hasMainCropForTargetYear = allCrops.some(
        (crop) =>
          crop.id !== cropToValidate.id &&
          crop.cropType === CROP_TYPE_FCPO.MAIN &&
          moment(crop.harvestEndDate).clone().year() === cropToValidate.harvestEndDate.clone().year() &&
          crop.cropId !== null
      );
      if (hasMainCropForTargetYear) {
        cropPeriodValidationErrors.push({ fieldId: field.id, message: t("cropForm.errorMessages.notAllowedMultipleMainCropsForSameYear") });
      }
    }

    if (cropPeriodValidationErrors.length) {
      // Find first error field and scroll to it
      const firstError = cropPeriodValidationErrors[0];
      const errorElement = document.querySelector(`[data-field-id="${firstError.fieldId}"]`);
      errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      setCropPeriodValidationErrors(cropPeriodValidationErrors);

      setTimeout(() => setCropPeriodValidationErrors([]), 4000);
      setIsLoading(false);
      message({ content: firstError.message, icon: "error" });
    } else {
      try {
        if (!formWithOnLoadValidation) {
          await form.validateFields(); // doing this to allow validation upon submission work as before
        }

        const formValues = form.getFieldsValue(true);

        const { seedingDateRange, harvestDateRange, ...restFormValues } = formValues.crop;

        const transformedCrop = {
          ...restFormValues,
          seedingStartDate: seedingDateRange?.[0] || null,
          seedingEndDate: seedingDateRange?.[1] || null,
          harvestStartDate: harvestDateRange?.[0] || null,
          harvestEndDate: harvestDateRange?.[1] || null,
          seedingVerificationStatus: existingCrop?.seedingVerificationStatus || null, // inject previous seeding verification status
        };

        const { crop, isTillageApplied, isMineralApplied, isOrganicApplied, hasFertilizerManagement, ...formPracticeObject } = {
          ...formValues,
          crop: transformedCrop,
        };

        const formPractices = Object.entries(formPracticeObject as FormPracticeObject).map(([_, practiceData]) => {
          const { dateRange, ...rest } = practiceData;

          return {
            ...rest,
            startDate: (dateRange as moment.Moment[])?.[0]?.toISOString(),
            endDate: (dateRange as moment.Moment[])?.[1]?.toISOString(),
          } as PracticeFCPO;
        });

        const requestBody = createCropPracticeRequestBody(crop, formPractices, isTillageApplied, isMineralApplied, isOrganicApplied, hasFertilizerManagement, [
          ...existingFertilizers,
          ...existingTillages,
        ]);

        if (existingCrop?.id && !isSideCropRecommendationPlaceholder) {
          updateFieldCropPractice({
            requestBody,
            cropId: existingCrop.id,
          });
        } else {
          addFieldCropPractice({
            requestBody: { ...requestBody, crop: { ...requestBody.crop, cropMixId: cmcMixCropFCPO.cropMixId } },
          });
        }

        const editingRequestComment = formValues.editingRequestComment?.[EDITING_REQUEST_TYPE.CROP];
        if (pendingCropVerificationRequest) {
          await submitEditingRequestComment({ requestId: pendingCropVerificationRequest.id, userComment: editingRequestComment ?? "" });
        }

        if (existingCrop.cropMixId) {
          navigate(basePath);
        }

        message({ content: t("cropForm.success"), icon: "tick" });
        setShowDrawer(false);

        if (showDrawerFromCMC) {
          setShowDrawerFromCMC(false);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const incomplete = () => {
    message({ content: t("errors.required"), icon: "error" });
  };

  const scrollToPracticeType = () => {
    switch (type) {
      case PRACTICE_TYPE.SEEDING:
        return seedingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      case PRACTICE_TYPE.TILLAGE:
        return tillageRef.current?.scrollIntoView({ behavior: "smooth", inline: "start" });
      case PRACTICE_TYPE.FERTILIZER:
        return fertilizerRef.current?.scrollIntoView({ behavior: "smooth", inline: "start" });
      default:
        return null;
    }
  };

  const bundleHasConfirmedData = (bundleSlug: string) => availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;

  const fieldIsEnrolled = isFieldEnrolled(carbonProgramResultOfField);

  const requirementConfig = getInputRequirementConfig(carbonProgram ?? null, carbonProgramResultOfField ?? null, existingCrop, fieldIsEnrolled);

  // Check requirement config to see if there are even possible optional inputs
  const hideOptionalPracticeToggle = !Object.values(requirementConfig || {}).some((level) => level === "optional");

  const isUndersowing = existingCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING;

  const checkShowEnrolmentNudgeCard = (bundleSlug: "tillage" | "fertilizer") => {
    const bundle = practiceBundles.find((b) => b.slug === bundleSlug);
    const bundleIsActive = !!bundle?.active;
    const bundleIsEnrollable = !!bundle?.enrollable;

    return isUndersowing && fieldIsEnrolled && isCurrentYear && !bundleIsActive && bundleIsEnrollable;
  };

  const showTillageEnrolmentNudgeCard = checkShowEnrolmentNudgeCard("tillage");
  const showFertilizerEnrolmentNudgeCard = checkShowEnrolmentNudgeCard("fertilizer");

  const cropYear = Number(existingCrop.year);

  // TODO Vali: possibly different admin logic
  const disableTillageAndSeedingInputs =
    (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.seedings, cropYear, activeFarmReportingYear, isDataSubmitted) ||
      getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.tillages, cropYear, activeFarmReportingYear, isDataSubmitted)) &&
    !!bundleHasConfirmedData("tillage") &&
    !getEnvironment().admin;

  const disableFertilizerInputs =
    (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.mineralFertilizers, cropYear, activeFarmReportingYear, isDataSubmitted) ||
      getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.organicFertilizers, cropYear, activeFarmReportingYear, isDataSubmitted)) &&
    !!bundleHasConfirmedData("fertilizer") &&
    !getEnvironment().admin;

  useEffect(() => {
    if (!farmIsEnrolledInCarbonProgram || simulationId) {
      setOptionalInputsAreShown(true);
    }
  }, [farmIsEnrolledInCarbonProgram, simulationId]);

  // Currently there is only one type of fertilizer management (4R), but I have set this up to handle multiple in the future
  // If undersowing or cover crop, we never want to show the input therefore we don't want to set the existing value
  const fertilizerManagement =
    existingCrop.cropType === CROP_TYPE_FCPO.MAIN
      ? fertilizerManagements.find((management) => management.year === year && management.fieldCropId === existingCrop.id)
      : null;

  const onClose = () => {
    if (fieldsTouched) {
      setShowModal(true);
    } else {
      setShowDrawerFromCMC(false);
      setShowDrawer(false);
    }
  };

  const onCropSelectionChanged = (crop: PracticeCrop) => {
    const recommendedDates = getCropRecommendedDates({ crop, selectedYear: year, cropType: existingCrop.cropType });
    if (recommendedDates) {
      setRecommendedSeedingDate(recommendedDates.recommendedSeedingDate.toISOString());
      setRecommendedHarvestDate(recommendedDates.recommendedHarvestDate.toISOString());
    }
  };

  // I guess this should help us check if practice bundle is active for the given year + baseline years (let's refactor this later if needed)
  const isPracticeBundleActiveForYear = useCallback(
    (bundleSlug: PRACTICE_BUNDLE_SLUG, year: number): boolean => {
      const bundle = practiceBundles.find((b) => b.slug === bundleSlug);

      if (!bundle?.active) {
        return false;
      }

      // I am mapping the bundle slug to practice types in baselineYearsByPracticeType
      let practiceTypeKeys: (keyof typeof baselineYearsByPracticeType)[] = [];

      if (bundleSlug === PRACTICE_BUNDLE_SLUG.TILLAGE) {
        practiceTypeKeys = ["tillages", "seedings"];
      } else if (bundleSlug === PRACTICE_BUNDLE_SLUG.FERTILIZER) {
        practiceTypeKeys = ["mineralFertilizers", "organicFertilizers"];
      }

      const isInBaselineYears = practiceTypeKeys.some((key) => {
        const baselineYears = baselineYearsByPracticeType[key];
        return baselineYears && baselineYears.includes(year);
      });

      if (isInBaselineYears) {
        return true;
      }

      if (bundle.startYear !== null && bundle.startYear !== undefined) {
        const yearOffset = isMandatoryCropCard ? 1 : 0;
        const maxYear = currentYear + yearOffset;
        const minYear = maxYear - 3; // TODO: use "required" from carbonRunnerResponse (FieldOutcomesByPractice) instead of hardcoding 3 years

        return year >= minYear && year <= maxYear;
      }

      return bundle.active;
    },
    [practiceBundles, baselineYearsByPracticeType, isMandatoryCropCard]
  );

  const getRefForFieldName = useCallback((fieldName: string[]): React.RefObject<HTMLDivElement> | null => {
    const fieldKey = fieldName[0];

    if (fieldKey === "crop" || fieldKey.startsWith("crop")) {
      return seedingRef;
      // eslint-disable-next-line no-else-return
    } else if (fieldKey.startsWith("tillage") || fieldKey === "isTillageApplied") {
      return tillageRef;
    } else if (
      fieldKey.startsWith("mineral") ||
      fieldKey.startsWith("organic") ||
      fieldKey === "isMineralApplied" ||
      fieldKey === "isOrganicApplied" ||
      fieldKey === "hasFertilizerManagement"
    ) {
      return fertilizerRef;
    }
    return null;
  }, []);

  const scrollToRef = useCallback((ref: React.RefObject<HTMLDivElement> | null) => {
    if (!ref?.current) return;

    const element = ref.current;
    const rect = element.getBoundingClientRect();
    const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isInViewport) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const validateYieldFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.yield)) return [];

    const fieldsWithErrors: FieldError[] = [];

    if (existingCrop.yieldTonsPerHa === null || existingCrop.yieldTonsPerHa === undefined) {
      fieldsWithErrors.push({
        name: ["crop", "yieldTonsPerHa"],
        errors: [t("cropForm.errorMessages.yieldRequired")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, existingCrop, t]);

  const validateSeedingTypeFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.seedings)) return [];

    if (!isPracticeBundleActiveForYear(PRACTICE_BUNDLE_SLUG.TILLAGE, cropYear)) {
      return [];
    }

    const fieldsWithErrors: FieldError[] = [];

    if (existingCrop.seedingPracticeType === null || existingCrop.seedingPracticeType === undefined) {
      fieldsWithErrors.push({
        name: ["crop", "seedingPracticeType"],
        errors: [t("cropForm.errorMessages.seedingTypeRequired")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, isPracticeBundleActiveForYear, cropYear, existingCrop.seedingPracticeType, t]);

  const validateTillageFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.tillages)) return [];

    if (!isPracticeBundleActiveForYear(PRACTICE_BUNDLE_SLUG.TILLAGE, cropYear)) {
      return [];
    }

    const fieldsWithErrors: FieldError[] = [];

    if (existingTillages.length === 0) {
      fieldsWithErrors.push({
        name: ["isTillageApplied"],
        errors: [t("tillageForm.errorMessages.tillageRequired")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, isPracticeBundleActiveForYear, cropYear, existingTillages.length, t]);

  const validateMineralFertilizerFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.mineralFertilizers)) return [];

    if (!isPracticeBundleActiveForYear(PRACTICE_BUNDLE_SLUG.FERTILIZER, cropYear)) {
      return [];
    }

    const fieldsWithErrors: FieldError[] = [];

    if (mineralFertilizers.length === 0) {
      fieldsWithErrors.push({
        name: ["isMineralApplied"],
        errors: [t("fertilizerForm.fertilizerRequired")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, isPracticeBundleActiveForYear, cropYear, mineralFertilizers.length, t]);

  const validateOrganicFertilizerFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.organicFertilizers)) return [];

    if (!isPracticeBundleActiveForYear(PRACTICE_BUNDLE_SLUG.FERTILIZER, cropYear)) {
      return [];
    }
    const fieldsWithErrors: FieldError[] = [];

    if (organicFertilizers.length === 0) {
      fieldsWithErrors.push({
        name: ["isOrganicApplied"],
        errors: [t("fertilizerForm.fertilizerRequired")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, isPracticeBundleActiveForYear, cropYear, organicFertilizers.length, t]);

  const validateFertilizerManagementFields = useCallback((): FieldError[] => {
    if (!requirementConfig || !isRequired(requirementConfig.fertilizerManagement) || existingCrop.cropType !== CROP_TYPE_FCPO.MAIN) {
      return [];
    }

    const fieldsWithErrors: FieldError[] = [];
    const fertilizerManagementValue = fertilizerManagement?.implements4R;
    const hasValidValue = fertilizerManagementValue === true || fertilizerManagementValue === false;

    if (!hasValidValue) {
      fieldsWithErrors.push({
        name: ["hasFertilizerManagement"],
        errors: [t("fertilizerForm.fertilizerManagement.required")],
      });
    }

    return fieldsWithErrors;
  }, [requirementConfig, existingCrop.cropType, fertilizerManagement, t]);

  // I am collating all validation functions into one for convenience
  const validateMissingRequiredFields = useCallback(() => {
    if (!requirementConfig) return null;

    const fieldsWithErrors: FieldError[] = [
      ...validateYieldFields(),
      ...validateSeedingTypeFields(),
      ...validateTillageFields(),
      ...validateMineralFertilizerFields(),
      ...validateOrganicFertilizerFields(),
      ...validateFertilizerManagementFields(),
    ];

    // Determine the first missing field ref for scrolling
    let firstMissingFieldRef: React.RefObject<HTMLDivElement> | null = null;
    if (fieldsWithErrors.length > 0) {
      firstMissingFieldRef = getRefForFieldName(fieldsWithErrors[0].name);
    }

    return { fieldsWithErrors, firstMissingFieldRef };
  }, [
    requirementConfig,
    validateYieldFields,
    validateSeedingTypeFields,
    validateTillageFields,
    validateMineralFertilizerFields,
    validateOrganicFertilizerFields,
    validateFertilizerManagementFields,
    getRefForFieldName,
  ]);

  const hasValidCropSelection = useMemo(() => !!existingCrop.cropId && existingCrop.cropId !== ZERO_UUID, [existingCrop.cropId]);

  // jst to reset validation state when drawer closes
  useEffect(() => {
    if (!showDrawer) {
      setInitialValidationDone(false);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (!formWithOnLoadValidation || !showDrawer || initialValidationDone || !requirementConfig || !fieldIsEnrolled) return;

    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;
    const attemptValidation = async (attempts: number = 0) => {
      if (isCancelled) return;

      // Check if refs are available, retry if not .. i.e: up to MAX_VALIDATION_ATTEMPTS)
      if (attempts < MAX_VALIDATION_ATTEMPTS) {
        const refsAvailable = seedingRef.current && tillageRef.current && fertilizerRef.current;

        if (!refsAvailable) {
          timeoutId = setTimeout(() => attemptValidation(attempts + 1), VALIDATION_DELAY_MS);
          return;
        }
      }

      if (!hasValidCropSelection) return;
      await form.validateFields();
      const validationResult = validateMissingRequiredFields();

      if (validationResult && validationResult.fieldsWithErrors.length > 0 && !isCancelled) {
        const errorFields = validationResult.fieldsWithErrors.map((field) => ({
          // from my research,`touch the field`  and setting field errors seems to be the best way to show errors immediately
          name: field.name,
          errors: field.errors,
          touched: true,
          validating: false,
        }));

        form.setFields(errorFields);

        if (validationResult.firstMissingFieldRef?.current) {
          scrollToRef(validationResult.firstMissingFieldRef);
        }
        setInitialValidationDone(true);
      }
    };
    timeoutId = setTimeout(() => attemptValidation(), VALIDATION_DELAY_MS);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    showDrawer,
    requirementConfig,
    initialValidationDone,
    formWithOnLoadValidation,
    validateMissingRequiredFields,
    form,
    scrollToRef,
    hasValidCropSelection,
    fieldIsEnrolled,
  ]);

  useEffect(() => {
    if (!formWithOnLoadValidation) {
      // 🚨 let's only scroll to practice type if we're not doing form validation on load
      // to avoid conflicting scroll behaviors
      const timeoutId = setTimeout(() => {
        scrollToPracticeType();
      }, DRAWER_SCROLL_DELAY_MS);

      return () => clearTimeout(timeoutId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Drawer
        dataTestId="add-edit-crop-drawer"
        title={t("titleDrawer")}
        visible={showDrawer}
        onClose={onClose}
        destroyOnHidden={true}
        className="add-edit-crop-drawer"
        zIndex={showDrawerFromCMC ? 1003 : undefined} // higher z-index to show it on top of the CMC drawer
      >
        <FieldHeadline
          headline={t("fieldHeadlineCard.headline")}
          field={field}
          shape={fieldShape}
          isLoading={fieldShapeLoading || isLoadingProgramOutcomes}
          isEnrolled={fieldIsEnrolled}
        />
        {existingCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING && (
          <HideFCPOFeatureForUnenrolledFarms>
            <PracticeBundleControls
              bundles={fieldIsEnrolled ? practiceBundles.filter((practiceBundle) => practiceBundle.slug !== PRACTICE_BUNDLE_SLUG.CROPS) : []}
              optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
              setOptionalInputsAreShown={setOptionalInputsAreShown}
              onBundleActiveStateChange={handleBundleActiveStateChange}
              isCurrentYear={isCurrentYear}
              onPastYearToggle={handlePastYearToggle}
              hideOptionalPractices={hideOptionalPracticeToggle}
              hidePracticeIcons={isInventoryProgram}
              viewType={viewType}
            />
          </HideFCPOFeatureForUnenrolledFarms>
        )}
        {isLoadingProgramOutcomes ? (
          <FormSkeleton cropType={existingCrop.cropType} />
        ) : (
          <Form
            form={form}
            name="cropForm"
            onFinish={onSubmit}
            onFinishFailed={incomplete}
            onValuesChange={() => setFieldsTouched(true)}
            preserve={true} // from my research, this helps to prevent re-validation from clearing touched state
          >
            {isSubmissionFeatureEnabled && !getEnvironment().admin && pendingCropVerificationRequest && (
              <EditingRequestBanner
                key={EDITING_REQUEST_TYPE.CROP}
                type={EDITING_REQUEST_TYPE.CROP}
                farmId={farmId!}
                id={existingCrop.id}
                requestId={pendingCropVerificationRequest.id}
              />
            )}

            <AddEditCropForm
              setShowDrawer={setShowDrawer}
              baselineYears={baselineYearsByPracticeType}
              crop={existingCrop}
              cropPeriodValidationErrors={cropPeriodValidationErrors}
              disableTillageAndSeedingInputs={disableTillageAndSeedingInputs}
              field={field}
              ref={seedingRef}
              onCropDelete={handleCropDeletion}
              recommendedSeedingDate={recommendedSeedingDate}
              recommendedHarvestDate={recommendedHarvestDate}
              onCropSelectionChanged={onCropSelectionChanged}
              year={year}
              optionalInputsAreShown={optionalInputsAreShown}
              requirementConfig={requirementConfig}
            />

            {/* Tillage Form */}
            {(shouldShowInput(requirementConfig?.tillages, optionalInputsAreShown) || existingTillages.length > 0) && (
              <>
                <AddEditTillageForm
                  cropId={existingCrop.id}
                  existingTillages={existingTillages}
                  ref={tillageRef}
                  disabled={disableTillageAndSeedingInputs}
                  baselineYears={baselineYearsByPracticeType.tillages}
                  recommendedSeedingDate={recommendedSeedingDate}
                  year={year}
                  requirementConfig={requirementConfig}
                  optionalInputsAreShown={optionalInputsAreShown}
                />
              </>
            )}

            {showTillageEnrolmentNudgeCard && !isInventoryProgram && (
              <PracticeEnrolmentNudgeCard
                practiceBundleSlug={PRACTICE_BUNDLE_SLUG.TILLAGE}
                onClick={() =>
                  handleBundleActiveStateChange(PRACTICE_BUNDLE_SLUG.TILLAGE, !practiceBundles.find((bundle) => bundle.slug === "tillage")?.active)
                }
                disabled={!!existingCrop.isConfirmed}
              />
            )}

            {/* Fertilizer Forms */}
            {(shouldShowInput(requirementConfig?.fertilizerManagement, optionalInputsAreShown) || fertilizerManagement) && (
              <FertilizerManagementYesNoForm
                initialValue={fertilizerManagement?.implements4R ?? null}
                requirementLevel={requirementConfig?.fertilizerManagement}
                disabled={!!existingCrop.isConfirmed}
              />
            )}

            {(shouldShowInput(requirementConfig?.mineralFertilizers, optionalInputsAreShown) || mineralFertilizers.length > 0) && (
              <AddEditOrganicAndMineralFertilizers
                chosenFertilizerType="mineral"
                disabled={disableFertilizerInputs}
                existingFertilizers={mineralFertilizers}
                ref={fertilizerRef}
                baselineYears={baselineYearsByPracticeType.mineralFertilizers}
                recommendedSeedingDate={recommendedSeedingDate}
                year={year}
                currency={carbonProgram?.currency}
                requirementLevel={requirementConfig?.mineralFertilizers}
              />
            )}

            {(shouldShowInput(requirementConfig?.organicFertilizers, optionalInputsAreShown) || organicFertilizers.length > 0) && (
              <AddEditOrganicAndMineralFertilizers
                chosenFertilizerType="organic"
                disabled={disableFertilizerInputs}
                existingFertilizers={organicFertilizers}
                ref={fertilizerRef}
                baselineYears={baselineYearsByPracticeType.organicFertilizers}
                recommendedSeedingDate={recommendedSeedingDate}
                year={year}
                currency={carbonProgram?.currency}
                requirementLevel={requirementConfig?.organicFertilizers}
              />
            )}

            {showFertilizerEnrolmentNudgeCard && !isInventoryProgram && (
              <PracticeEnrolmentNudgeCard
                practiceBundleSlug={PRACTICE_BUNDLE_SLUG.FERTILIZER}
                onClick={() =>
                  handleBundleActiveStateChange(PRACTICE_BUNDLE_SLUG.FERTILIZER, !practiceBundles.find((bundle) => bundle.slug === "fertilizer")?.active)
                }
                disabled={!!existingCrop.isConfirmed}
              />
            )}

            <StickyFooterButtons absolute>
              <div className="row-space-between">
                <Button dataTestId="save-crop-drawer" loading={isLoading} type="primary" htmlType="submit">
                  {t("saveCropDrawer")}
                </Button>
              </div>
            </StickyFooterButtons>
          </Form>
        )}
      </Drawer>

      <Modal
        showModal={showModal}
        onOk={() => {
          onSubmit();
          setShowModal(false);
        }}
        onCancel={() => {
          setShowModal(false);
          setShowDrawer(false);
        }}
        okText={t("saveCropDrawerSave")}
        cancelText={t("saveCropDrawerDiscard")}
        zIndex={10002}
        destroyOnHidden={true}
        content={
          <>
            <h3>{t("saveCropDrawerConfirm")}</h3>
          </>
        }
      />
    </>
  );
};

const FormSkeleton = ({ cropType }: { cropType: CropFCPO["cropType"] }) => {
  if (cropType === CROP_TYPE_FCPO.UNDERSOWING) {
    return <Skeleton type="block" height="15rem" className="margin-top-200" />;
  }

  return (
    <>
      <Skeleton type="block" height="15rem" className="margin-top-200" />
      <Skeleton type="block" height="5rem" />
      <Skeleton type="block" height="5rem" />
    </>
  );
};
